import * as React from "react";
import {connect} from "react-redux";
import {none, Option, option} from "ts-option";
import {FormBodyProps} from "../../../common/props/FormBodyProps";
import PartnerBrandForm from "../../../partnerBrand/PartnerBrandForm";
import InputFormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/InputFormGroup";
import FormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import DropDown from "../../../../hiberUiKit/src/molecules/DropDown/DropDown";
import {Icon} from "../../../../hiberUiKit/src/atoms/Icon/Icon";
import Row from "../../../layout/Row";
import Col from "../../../layout/Col";
import AddressFormGroup from "../../../address/form/component/AddressFormGroup";
import TextAreaFormGroup from "../../../ui/form/TextAreaFormGroup";
import BooleanFormGroup from "../../../booleanFormGroup.tsx/BooleanFormGroup";
import BDPSelectorFormGroup from "../../../lead/form/BDPSelectorFormGroup";
import BrandFormGroup from "../../../user/form/BrandFormGroup";
import UtilityFormGroup from "../../utilities/form/UtilityFormGroup";
import LUSelectorFormGroup from "../LUSelectorFormGroup";
import ManualAddressInputModal from "../form/address/ManualAddressInputModal";
import Brand from "../../../../model/brand/Brand";
import {PartnerBrandCurrency} from "../../../../model/partnerBrand/PartnerBrand";
import {RootState} from "../../../../store/reducer/root/RootState";
import {createEmptyMatrixState, DealerPriceMatrixState} from "../../../brand/dto/DealerPriceMatrixState";
import {PartnerBrandSubsectionTitle} from "./PartnerBrandSectionTitle";
import PartnerBrandLogoField from "./PartnerBrandLogoField";
import "./partnerBrandPage.scss";

interface ComponentProps {
	/**
	 * Partner brand identifier, empty while the brand is being created.
	 */
	partnerBrandId: Option<string>;
	/**
	 * On add logo file.
	 */
	onAddFile: (file: File) => any;
	/**
	 * On remove logo file.
	 */
	onRemoveFile: () => any;
	/**
	 * On dealer price matrix change. Changing the parent brand resets the matrix.
	 */
	onDealerPriceMatrixChange: (state: DealerPriceMatrixState) => void;
}

interface ReduxProps {
	brands: Option<Brand[]>;
}

interface State {
	/**
	 * Indicates if manual address input modal is visible.
	 */
	addressModalVisible: boolean;
}

interface PartnerBrandCurrencyDropDownValue {
	label: string,
	value: Option<PartnerBrandCurrency>
}

type Props = ComponentProps & FormBodyProps<PartnerBrandForm> & ReduxProps;

/**
 * Who the partner is: identity, contact details, team, logo and notes.
 */
class PartnerBrandCompanySection extends React.PureComponent<Props, State> {
	currencyItems: PartnerBrandCurrencyDropDownValue[] = [
		{label: "EUR", value: option(PartnerBrandCurrency.EUR)},
		{label: "GBP", value: option(PartnerBrandCurrency.GBP)}
	];

	constructor(props: Props) {
		super(props);
		this.state = {
			addressModalVisible: false
		};
	}

	render() {
		const form = this.props.form;
		return (
			<>
				<PartnerBrandSubsectionTitle title={"Details"} first={true}/>
				<Row>
					<Col lg={4} xl={4}>
						<BrandFormGroup label={"Parent brand"}
										field={form.brandId}
										onChange={this.onBrandChange}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Name"
										placeholder="Enter brand name"
										field={form.name}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.name)}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Trading name"
										placeholder="Enter brand trading name"
										field={form.tradingName}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.tradingName)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={8} xl={8}>
						<AddressFormGroup label="Business address"
										  field={form.address}
										  onChange={field => {
											  field.getOptionalValue().isDefined ? this.props.onFieldCompleted(field) :
												  this.props.onFieldChange(field)
										  }}
										  form={form}
										  onFocusChange={this.props.onFocusChange}
										  onIconClick={this.openAddressModal}
										  icon={Icon.Edit}
										  iconTooltipText={"Enter address manually"}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Phone"
										placeholder="Enter brand phone number"
										field={form.phoneNumber}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.phoneNumber)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Email"
										placeholder="Enter brand email address"
										field={form.email}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.email)}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Website"
										placeholder="Enter brand website"
										field={form.domain}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.domain)}/>
					</Col>
					<Col lg={4} xl={4}>
						<FormGroup label={"Currency"}
								   error={form.currency.firstError().orNull}
								   customClass={"c-boolean-form-group"}>
							<DropDown items={this.currencyItems}
									  itemToLabel={(item: PartnerBrandCurrencyDropDownValue) => item.label}
									  itemToId={(item: PartnerBrandCurrencyDropDownValue) => item.value.getOrElse("-1")}
									  onSelect={this.onCurrencyChange}
									  selectedItemId={form.currency.getOptionalValue().getOrElse("-1")}/>
						</FormGroup>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<BooleanFormGroup field={form.vatRegistered}
										  onChange={this.props.onFieldChange}
										  label={"VAT registered"}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="VAT number"
										placeholder="Enter VAT Registration Number"
										field={form.vatRegistrationNumber}
										onChange={this.props.onFieldChange}
										focus={form.hasFocus(form.vatRegistrationNumber)}/>
					</Col>
					<Col lg={4} xl={4}>
						<UtilityFormGroup label={"Default Utility"}
										  placeholder={"Select default utility"}
										  field={form.defaultUtilityId}
										  onChange={this.props.onFieldChange}
										  clearable={true}/>
					</Col>
				</Row>
				<PartnerBrandSubsectionTitle title={"Team"}/>
				<Row>
					<Col lg={4} xl={4}>
						<BDPSelectorFormGroup label={"Business Development Partner"}
											  onChange={this.props.onFieldChange}
											  field={form.bdpId}/>
					</Col>
					{this.props.partnerBrandId.isDefined && <Col lg={4} xl={4}>
						<LUSelectorFormGroup label={"Lead User"}
											 onChange={this.props.onFieldChange}
											 field={form.leadUserId}
											 partnerBrandId={this.props.partnerBrandId}/>
					</Col>}
				</Row>
				<PartnerBrandSubsectionTitle title={"Logo"}/>
				<PartnerBrandLogoField logoId={form.logoId.getOptionalValue()}
									   onDropFile={this.props.onAddFile}
									   onRemove={this.props.onRemoveFile}/>
				<PartnerBrandSubsectionTitle title={"Notes"}/>
				<Row>
					<Col lg={6} xl={6}>
						<TextAreaFormGroup label="Important"
										   placeholder="Important partner brand notes"
										   field={form.noteImportant}
										   onChange={this.props.onFieldChange}
										   focus={form.hasFocus(form.noteImportant)}
										   rows={4}/>
					</Col>
					<Col lg={6} xl={6}>
						<TextAreaFormGroup label="Other"
										   placeholder="Brand notes"
										   field={form.freeText}
										   onChange={this.props.onFieldChange}
										   focus={form.hasFocus(form.freeText)}
										   rows={4}/>
					</Col>
				</Row>
				{this.state.addressModalVisible &&
				<ManualAddressInputModal label="Enter address"
										 field={form.address}
										 onChange={this.props.onFieldCompleted}
										 form={form}
										 closeModal={this.closeAddressModal}/>}
			</>
		);
	}

	openAddressModal = () => {
		this.setState({addressModalVisible: true});
	}

	closeAddressModal = () => {
		this.setState({addressModalVisible: false});
	}

	onCurrencyChange = (item: PartnerBrandCurrencyDropDownValue) => {
		this.props.onFieldChange(this.props.form.currency.updateValue(item.value));
	}

	/**
	 * A new parent brand brings its own payout amounts: the heatpump and flowsave amounts are copied from it
	 * and the registration matrix is cleared, so that it inherits the new brand's prices.
	 */
	onBrandChange = (field: any) => {
		const brandId = field.getValue();
		const selectedBrand = this.props.brands
			.flatMap(brands => option(brands.filter(b => b.id === brandId)[0]));

		if (selectedBrand.isDefined) {
			selectedBrand.forEach(brand => {
				const fieldsToUpdate = [
					field,
					this.props.form.creditValueAirToWaterHp.updateValue(
						brand.creditValueAirToWaterHp.map(v => (v / 100).toFixed(2))
					),
					this.props.form.creditValueAirToAirHp.updateValue(
						brand.creditValueAirToAirHp.map(v => (v / 100).toFixed(2))
					),
					this.props.form.creditValueWaterToWaterHp.updateValue(
						brand.creditValueWaterToWaterHp.map(v => (v / 100).toFixed(2))
					),
					this.props.form.creditValueBrineToWaterHp.updateValue(
						brand.creditValueBrineToWaterHp.map(v => (v / 100).toFixed(2))
					),
					this.props.form.creditValueFlowsave.updateValue(
						brand.creditValueFlowsave.map(v => (v / 100).toFixed(2))
					)
				];
				this.props.onFieldsChange(fieldsToUpdate);
				this.props.onDealerPriceMatrixChange(createEmptyMatrixState());
			});
		} else {
			this.props.onFieldChange(field);
		}
	}
}

function mapStateToProps(state: RootState): ReduxProps {
	return {
		brands: state.brand.brands
	};
}

export default connect(mapStateToProps)(PartnerBrandCompanySection);
